import { useEditManager } from "@/composables/edit-manager";
import config from "@/config";
import { types } from "@/lib/openapi";
import { useAuthenticationStore } from "@/stores/authentication-store";
import { useCurrentEntityManager } from "@/stores/entity-store";
import { useUserManager } from "@/stores/user-store";
import { defineStore, storeToRefs } from "pinia";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";

export type UserActivity = types.UserActivity;
export type UserActivityUpdate = types.ActivityUpdate;

export const usePresenceStore = defineStore("presence", () => {
    const { user } = useUserManager();
    const { getToken } = useAuthenticationStore();
    const { isEditing } = useEditManager();
    const { entity } = useCurrentEntityManager();

    const socket = ref<WebSocket>();
    const isConnected = ref(false);
    const heartbeatInterval = ref<number | null>(null);
    const currentUsers = ref<UserActivity[]>([]);

    // Use the configuration value
    const HEARTBEAT_INTERVAL_MS = config.activity.heartbeatIntervalMs;

    const editingUsers = computed(() =>
        currentUsers.value.filter((user) => user.is_editing)
    );
    const viewingUsers = computed(() =>
        currentUsers.value.filter((user) => !user.is_editing)
    );

    const publishState = () => {
        if (
            socket.value?.readyState !== WebSocket.OPEN ||
            !user.value?.identity
        )
            return;

        const activity: UserActivityUpdate = {
            entity_id: entity.value?.identity || null,
            is_editing: isEditing.value,
        };

        socket.value?.send(JSON.stringify(activity));
    };

    const startHeartbeat = () => {
        // Clear any existing interval
        stopHeartbeat();

        // Set up new interval to publish state periodically
        heartbeatInterval.value = window.setInterval(() => {
            publishState();
        }, HEARTBEAT_INTERVAL_MS);
    };

    const stopHeartbeat = () => {
        if (heartbeatInterval.value) {
            clearInterval(heartbeatInterval.value);
            heartbeatInterval.value = null;
        }
    };

    const connect = async () => {
        const userId = user.value?.identity;
        const authToken = await getToken();
        if (!userId || !authToken) return;

        const baseUrl = import.meta.env.VITE_API_URL;
        const wsProtocol = baseUrl.startsWith("https") ? "wss" : "ws";
        const wsBaseUrl = baseUrl.replace(/^https?:\/\//, `${wsProtocol}://`);

        const url = new URL(`${wsBaseUrl}/stek/activity_mgt/activity/start`);
        url.searchParams.append("token", authToken);

        socket.value = new WebSocket(url.toString());

        socket.value.onopen = () => {
            isConnected.value = true;
            publishState();
            startHeartbeat();
        };

        socket.value.onmessage = (event) => {
            try {
                const activities = JSON.parse(event.data);
                if (Array.isArray(activities)) {
                    // Activities already match the UserActivity type, no need for conversion
                    currentUsers.value = activities;
                }
            } catch (error) {
                console.error("Failed to parse WebSocket message:", error);
            }
        };

        socket.value.onclose = () => {
            socket.value = undefined;
            isConnected.value = false;
            attemptReconnect();
        };

        socket.value.onerror = () => {
            isConnected.value = false;
        };
    };

    const attemptReconnect = () => {
        setTimeout(() => {
            connect();
        }, 1000);
    };

    const disconnect = () => {
        stopHeartbeat();
        socket.value?.close();
        socket.value = undefined;
        isConnected.value = false;
    };

    onMounted(async () => {
        await connect();
    });

    onUnmounted(() => {
        disconnect();
    });

    // Watch only the user identity for connection management
    watch(
        () => user.value?.identity,
        async (newUserId, oldUserId) => {
            if (!newUserId || newUserId === oldUserId) return;

            if (isConnected.value) {
                disconnect();
            }
            await connect();
        },
        { immediate: true }
    );

    // Watch specific properties for state publishing
    watch(
        [
            isEditing,
            () => entity.value?.identity,
            () => socket.value?.readyState,
            () => user.value?.identity,
        ],
        () => {
            publishState();
        }
    );

    return {
        currentUsers,
        editingUsers,
        viewingUsers,
        isConnected,
        connect,
        disconnect,
    };
});

export const usePresenceManager = () => {
    return storeToRefs(usePresenceStore());
};
